import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { 
  Star, 
  MessageSquare, 
  Edit,
  Trash2,
  AlertCircle,
  Plus,
  Calendar,
  User,
  BookOpen
} from 'lucide-react';

interface Feedback {
  id: number;
  fromUser: {
    id: number;
    username: string;
    fullName: string;
  };
  toUser: {
    id: number;
    username: string;
    fullName: string;
  };
  session: {
    id: number;
    skill: {
      id: number;
      name: string;
    };
  };
  rating: number;
  comment: string;
  createdAt: string;
}

interface FeedbackStats {
  feedback: Feedback[];
  averageRating: number;
  totalCount: number;
}

interface LearningSession {
  id: number;
  teacher: {
    id: number;
    username: string;
    fullName: string;
  };
  learner: {
    id: number;
    username: string;
    fullName: string;
  };
  skill: {
    id: number;
    name: string;
  };
  status: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  credits: number;
}

const Feedback: React.FC = () => {
  const { user } = useAuth();
  const [receivedFeedback, setReceivedFeedback] = useState<FeedbackStats | null>(null);
  const [givenFeedback, setGivenFeedback] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'received' | 'given'>('received');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<Feedback | null>(null);
  const [availableSessions, setAvailableSessions] = useState<LearningSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<LearningSession | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<{
    toUserId: number;
    sessionId: number;
    skillId: number;
    rating: number;
    comment: string;
  }>();

  const rating = watch('rating');

  useEffect(() => {
    fetchFeedback();
    fetchAvailableSessions();
  }, []);

  const fetchAvailableSessions = async () => {
    try {
      const response = await apiService.getSessions();
      // Filter for completed sessions where user can give feedback
      const sessions = response.data || [];
      const completedSessions = sessions.filter((session: LearningSession) => 
        session.status === 'COMPLETED' && 
        (session.teacher.id === user?.id || session.learner.id === user?.id)
      );
      setAvailableSessions(completedSessions);
    } catch (error: any) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchFeedback = async () => {
    try {
      setIsLoading(true);
      const [receivedResponse, givenResponse] = await Promise.all([
        apiService.getFeedback(),
        apiService.getFeedbackGiven()
      ]);

      setReceivedFeedback(receivedResponse.data);
      setGivenFeedback(givenResponse.data || []);
    } catch (error: any) {
      toast.error('Failed to load feedback');
      console.error('Error fetching feedback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateFeedback = async (data: any) => {
    if (!selectedSession) {
      toast.error('Please select a session first');
      return;
    }

    if (!data.rating || data.rating < 1) {
      toast.error('Please select a rating (1-5 stars)');
      return;
    }

    try {
      // Determine who the feedback is for
      const isUserTeacher = selectedSession.teacher.id === user?.id;
      const feedbackData = {
        toUserId: isUserTeacher ? selectedSession.learner.id : selectedSession.teacher.id,
        sessionId: selectedSession.id,
        skillId: selectedSession.skill.id,
        rating: data.rating,
        comment: data.comment
      };

      console.log('Submitting feedback data:', feedbackData);
      await apiService.createFeedback(feedbackData);
      setShowFeedbackModal(false);
      setSelectedSession(null);
      reset();
      fetchFeedback();
      fetchAvailableSessions(); // Refresh available sessions
      toast.success('Feedback submitted successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to submit feedback');
    }
  };

  const handleUpdateFeedback = async (feedbackId: number, data: any) => {
    try {
      await apiService.updateFeedback(feedbackId, data);
      setEditingFeedback(null);
      reset();
      fetchFeedback();
      toast.success('Feedback updated successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update feedback');
    }
  };

  const handleDeleteFeedback = async (feedbackId: number) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;

    try {
      await apiService.deleteFeedback(feedbackId);
      fetchFeedback();
      toast.success('Feedback deleted successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete feedback');
    }
  };

  const renderStars = (rating: number, interactive = false, onRatingChange?: (rating: number) => void) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? () => onRatingChange?.(star) : undefined}
            className={`${
              interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'
            } transition-transform`}
          >
            <Star
              className={`h-5 w-5 ${
                star <= rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingText = (rating: number) => {
    switch (rating) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Excellent';
      default: return 'Not Rated';
    }
  };

  const formatSessionDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSessionRole = (session: LearningSession) => {
    if (session.teacher.id === user?.id) {
      return 'Teacher';
    } else if (session.learner.id === user?.id) {
      return 'Learner';
    }
    return 'Participant';
  };

  const getFeedbackRecipient = (session: LearningSession) => {
    if (session.teacher.id === user?.id) {
      return session.learner;
    } else {
      return session.teacher;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text">Loading Feedback...</h2>
          <p className="text-gray-600">Preparing your reviews and ratings</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold gradient-text">Feedback & Reviews</h1>
            <p className="text-gray-600 mt-2 text-lg">Manage your feedback and ratings</p>
          </div>
          <div className="flex items-center space-x-4">
            {receivedFeedback && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Average Rating</p>
                <div className="flex items-center space-x-2">
                  <p className="text-lg font-semibold text-gray-900">
                    {receivedFeedback.averageRating.toFixed(1)}
                  </p>
                  {renderStars(Math.round(receivedFeedback.averageRating))}
                </div>
              </div>
            )}
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Reviews</p>
              <p className="text-lg font-semibold text-blue-600">
                {receivedFeedback?.totalCount || 0}
              </p>
            </div>
            <button
              onClick={() => setShowFeedbackModal(true)}
              disabled={availableSessions.length === 0}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={availableSessions.length === 0 ? 'No completed sessions available for feedback' : 'Give feedback for completed sessions'}
            >
              <Plus className="h-4 w-4 mr-2" />
              Give Feedback
              {availableSessions.length > 0 && (
                <span className="ml-2 bg-white bg-opacity-20 px-2 py-1 rounded-full text-xs">
                  {availableSessions.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Available Sessions for Feedback */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Sessions Available for Feedback</h2>
        {availableSessions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableSessions.map((session) => (
              <div key={session.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <BookOpen className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">{session.skill.name}</span>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    getSessionRole(session) === 'Teacher' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {getSessionRole(session)}
                  </span>
                </div>
                
                <div className="space-y-2 mb-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>{formatSessionDateTime(session.scheduledStartTime)}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <User className="h-4 w-4" />
                    <span>With: {getFeedbackRecipient(session).fullName || getFeedbackRecipient(session).username}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Credits: {session.credits}
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedSession(session);
                    setShowFeedbackModal(true);
                    reset({ rating: 0, comment: '' });
                    setValue('rating', 0);
                  }}
                  className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                >
                  Give Feedback
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No completed sessions available for feedback</p>
            <p className="text-sm text-gray-400 mt-1">Complete a learning session to give feedback</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('received')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'received'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Received Feedback ({receivedFeedback?.totalCount || 0})
            </button>
            <button
              onClick={() => setActiveTab('given')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'given'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Given Feedback ({givenFeedback.length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'received' && receivedFeedback && (
            <div className="space-y-4">
              {receivedFeedback.feedback.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No feedback received yet</p>
                </div>
              ) : (
                receivedFeedback.feedback.map((feedback) => (
                  <div key={feedback.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {feedback.fromUser.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {feedback.fromUser.fullName || feedback.fromUser.username}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {feedback.session.skill.name} • {formatDate(feedback.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-2">
                          {renderStars(feedback.rating)}
                          <span className={`text-sm font-medium ${getRatingColor(feedback.rating)}`}>
                            {getRatingText(feedback.rating)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {feedback.comment && (
                      <div className="mt-4 pl-13">
                        <p className="text-gray-700">{feedback.comment}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'given' && (
            <div className="space-y-4">
              {givenFeedback.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No feedback given yet</p>
                </div>
              ) : (
                givenFeedback.map((feedback) => (
                  <div key={feedback.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {feedback.toUser.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {feedback.toUser.fullName || feedback.toUser.username}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {feedback.session.skill.name} • {formatDate(feedback.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-right">
                          <div className="flex items-center space-x-2">
                            {renderStars(feedback.rating)}
                            <span className={`text-sm font-medium ${getRatingColor(feedback.rating)}`}>
                              {getRatingText(feedback.rating)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              setEditingFeedback(feedback);
                              reset({
                                toUserId: feedback.toUser.id,
                                sessionId: feedback.session.id,
                                skillId: feedback.session.skill.id,
                                rating: feedback.rating,
                                comment: feedback.comment
                              });
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteFeedback(feedback.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {feedback.comment && (
                      <div className="mt-4 pl-13">
                        <p className="text-gray-700">{feedback.comment}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {(showFeedbackModal || editingFeedback) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingFeedback ? 'Edit Feedback' : 'Give Feedback'}
            </h2>

            {/* Show session information if creating new feedback */}
            {!editingFeedback && selectedSession && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="font-medium text-blue-900 mb-2">Session Details</h3>
                <div className="space-y-1 text-sm text-blue-800">
                  <p><span className="font-medium">Skill:</span> {selectedSession.skill.name}</p>
                  <p><span className="font-medium">Date:</span> {formatSessionDateTime(selectedSession.scheduledStartTime)}</p>
                  <p><span className="font-medium">Your Role:</span> {getSessionRole(selectedSession)}</p>
                  <p><span className="font-medium">Feedback for:</span> {getFeedbackRecipient(selectedSession).fullName || getFeedbackRecipient(selectedSession).username}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(editingFeedback ? 
              (data) => handleUpdateFeedback(editingFeedback.id, data) : 
              handleCreateFeedback
            )}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rating *
                  </label>
                  <div className="flex items-center space-x-2">
                    {renderStars(rating || 0, true, (newRating) => {
                      setValue('rating', newRating);
                    })}
                    <span className="text-sm text-gray-500">
                      {rating ? `${rating} star${rating > 1 ? 's' : ''}` : 'Click to rate'}
                    </span>
                  </div>
                  {errors.rating && (
                    <p className="mt-1 text-sm text-red-600">{errors.rating.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comment *
                  </label>
                  <textarea
                    {...register('comment', { required: 'Comment is required' })}
                    rows={4}
                    placeholder="Share your experience with this session..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    {...register('rating', { 
                      required: 'Rating is required',
                      min: { value: 1, message: 'Rating must be at least 1 star' },
                      max: { value: 5, message: 'Rating cannot exceed 5 stars' }
                    })}
                    type="hidden"
                  />
                  {errors.comment && (
                    <p className="mt-1 text-sm text-red-600">{errors.comment.message}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowFeedbackModal(false);
                    setEditingFeedback(null);
                    setSelectedSession(null);
                    reset();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  {editingFeedback ? 'Update' : 'Submit'} Feedback
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback; 